import { useEffect, useRef } from "react";

import config from "@/config";
import fetchWrapper from "@/services/fetchWrapper.service";

import { getArtworkTransactionSource } from "../utils/artworkView.selectors";

import {
  hasTransactionHistory,
  isLikelySoldArtwork,
  markArtworkForViewer,
  matchesArtworkRouteContext,
  resolveArtworkSlug,
} from "./artworkFeedState.utils";

export default function useArtworkRecoveryEffects({
  artworks,
  activeSlide,
  slug,
  viewerSessionId,
  setArtworks,
  cacheHandler,
  listCacheKeyRef,
  locationCacheKey,
  buildArtworkEndpoint,
  resolveHoldingContext,
  pruneInaccessibleArtworkFromFeed,
  cacheInvalidationActions,
}) {
  // A response can still lack transaction history. Keep the key after the
  // recovery attempt so that response does not re-trigger the same request.
  const attemptedMissingDetailsRecoveryRef = useRef(new Set());
  const refreshedAuctionsRef = useRef({});
  const pendingListCacheRef = useRef(false);

  useEffect(() => {
    const currentArtwork = artworks?.data?.[activeSlide];
    if (!currentArtwork) {
      return;
    }

    // The route loader owns the initial active-artwork request. Recovery is
    // only meaningful after that canonical response has been applied; running
    // it against a card/list snapshot creates a second competing request on
    // page entry.
    if (currentArtwork._enriched !== true) {
      return;
    }

    // A canonical route response already contains the latest viewer-aware
    // artwork payload. Fetching it again because an optional transaction
    // summary is absent cannot recover additional data and causes a visible
    // second state replacement for sold artworks.
    const hasViewerHydrationMarker = Object.prototype.hasOwnProperty.call(
      currentArtwork,
      "_viewerStateHydratedFor",
    );
    if (
      hasViewerHydrationMarker
      && currentArtwork._viewerStateHydratedFor === (viewerSessionId ?? null)
    ) {
      return;
    }

    const hasTransactions = hasTransactionHistory(currentArtwork, getArtworkTransactionSource);
    const likelySold = isLikelySoldArtwork(currentArtwork);

    if (hasTransactions || !likelySold) {
      return;
    }

    const slugToFetch = resolveArtworkSlug(currentArtwork, slug);
    if (!slugToFetch) {
      return;
    }

    const artworkHolding = resolveHoldingContext(currentArtwork);
    const missingDetailsKey = artworkHolding.holdingId ? `${slugToFetch}::${artworkHolding.holdingId}` : slugToFetch;

    if (attemptedMissingDetailsRecoveryRef.current.has(missingDetailsKey)) {
      return;
    }

    let isActive = true;
    attemptedMissingDetailsRecoveryRef.current.add(missingDetailsKey);

    fetchWrapper.get(buildArtworkEndpoint(slugToFetch, artworkHolding), {
      injectToken: true,
    })
      .then(({ response, data }) => {
        if (!isActive) {
          return;
        }

        if (!response.ok || !data) {
          if (response.status === 404) {
            pruneInaccessibleArtworkFromFeed(
              {
                slug: slugToFetch,
                holdingId: artworkHolding.holdingId,
                ownerId: artworkHolding.ownerId,
                holdingType: artworkHolding.holdingType,
              },
              { reason: "missing-details-fetch-404" },
            );
          }
          return;
        }

        const hydratedData = markArtworkForViewer(data, viewerSessionId);

        pendingListCacheRef.current = true;
        setArtworks((prev) => {
          if (!prev || !Array.isArray(prev.data)) {
            return prev;
          }

          const indexToUpdate = prev.data.findIndex(
            (item) => matchesArtworkRouteContext(item, {
              slug: slugToFetch,
              holdingId: artworkHolding.holdingId,
              ownerId: artworkHolding.ownerId,
              holdingType: artworkHolding.holdingType,
            }),
          );

          if (indexToUpdate === -1) {
            return prev;
          }

          const nextData = [...prev.data];
          nextData[indexToUpdate] = { ...nextData[indexToUpdate], ...hydratedData };
          const nextState = { ...prev, data: nextData };

          return nextState;
        });

        const cacheKeyForArtwork = artworkHolding.holdingId
          ? `artwork_${slugToFetch}_holding_${artworkHolding.holdingId}`
          : `artwork_${slugToFetch}`;
        cacheHandler.storeInCache(cacheKeyForArtwork, {
          totalDocs: 1,
          data: [hydratedData],
          viewContext: artworkHolding.holdingId ? { type: "holding", ...artworkHolding } : undefined,
        }, {
          invalidateWhen: cacheInvalidationActions,
        });
      })
      .catch(() => {
        if (!isActive) {
          return;
        }
      });

    return () => {
      isActive = false;
    };
  }, [
    activeSlide,
    artworks,
    buildArtworkEndpoint,
    cacheHandler,
    cacheInvalidationActions,
    locationCacheKey,
    pruneInaccessibleArtworkFromFeed,
    resolveHoldingContext,
    setArtworks,
    slug,
    viewerSessionId,
    listCacheKeyRef,
  ]);

  useEffect(() => {
    if (!pendingListCacheRef.current || !artworks) {
      return;
    }
    pendingListCacheRef.current = false;

    const keyToStore = listCacheKeyRef.current || locationCacheKey;
    if (keyToStore) {
      cacheHandler.storeInCache(keyToStore, artworks, {
        invalidateWhen: cacheInvalidationActions,
      });
    }
  }, [artworks, cacheHandler, cacheInvalidationActions, listCacheKeyRef, locationCacheKey]);

  useEffect(() => {
    if (!artworks || !Array.isArray(artworks.data) || artworks.data.length === 0) {
      return;
    }

    const activeArtwork = artworks.data[activeSlide];
    if (!activeArtwork || !activeArtwork.slug) {
      return;
    }

    const about = activeArtwork.artwork_about ?? {};
    const auction = activeArtwork.artwork_auction ?? {};
    const isAuctionArtwork = Boolean(about.artwork_is_auction);
    if (!isAuctionArtwork) {
      return;
    }

    const parseDate = (value) => {
      if (!value) return null;
      const timestamp = new Date(value).getTime();
      return Number.isNaN(timestamp) ? null : timestamp;
    };

    const now = Date.now();
    const startsAtTs = parseDate(auction.starts_at);
    const endsAtTs = parseDate(auction.ends_at);
    const hasStarted = startsAtTs === null || startsAtTs <= now;
    const hasNotEnded = endsAtTs === null || endsAtTs > now;
    const withinSchedule = hasStarted && hasNotEnded;

    const rawEnabled = auction.is_enabled;
    if (rawEnabled === true || !withinSchedule) {
      return;
    }

    const refreshKey = activeArtwork._id || activeArtwork.slug;
    const lastRefreshAt = refreshedAuctionsRef.current[refreshKey];
    if (lastRefreshAt && now - lastRefreshAt < 30000) {
      return;
    }
    refreshedAuctionsRef.current[refreshKey] = now;

    let cancelled = false;

    fetchWrapper
      .get(`${config.apis.api.url}/artwork/${activeArtwork.slug}`, {
        injectToken: true,
      })
      .then(({ response, data }) => {
        if (cancelled || !response.ok || !data) {
          return;
        }

        updateArtworkInCaches({
          artworkData: data,
          matchByIdentity: (item) => {
            if (item._id && data._id) {
              return item._id === data._id;
            }
            return item.slug === data.slug;
          },
          viewerSessionId,
          cacheHandler,
          listCacheKeyRef,
          locationCacheKey,
          cacheInvalidationActions,
        });
      })
      .catch((error) => {
        if (!cancelled) {
          console.error("[ARTWORK] Failed to refresh auction status", error);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [activeSlide, artworks, cacheHandler, cacheInvalidationActions, locationCacheKey, setArtworks, viewerSessionId]);

  function updateArtworkInCaches({
    artworkData,
    matchByIdentity,
    viewerSessionId: currentViewerSessionId,
    cacheHandler: currentCacheHandler,
    listCacheKeyRef: currentListCacheKeyRef,
    locationCacheKey: currentLocationCacheKey,
    cacheInvalidationActions: currentCacheInvalidationActions,
  }) {
    const hydratedData = markArtworkForViewer(artworkData, currentViewerSessionId);

    setArtworks((previous) => {
      if (!previous || !Array.isArray(previous.data)) {
        return previous;
      }

      const index = previous.data.findIndex(matchByIdentity);
      if (index === -1) {
        return previous;
      }

      const updatedData = [...previous.data];
      updatedData[index] = {
        ...previous.data[index],
        ...hydratedData,
      };

      const updatedPayload = {
        ...previous,
        data: updatedData,
      };

      const listCacheKey = currentListCacheKeyRef.current || currentLocationCacheKey;
      if (listCacheKey) {
        currentCacheHandler.storeInCache(listCacheKey, updatedPayload, {
          invalidateWhen: currentCacheInvalidationActions,
        });
      }

      if (artworkData.slug) {
        currentCacheHandler.storeInCache(
          `artwork_${artworkData.slug}`,
          { totalDocs: 1, data: [hydratedData] },
          {
            invalidateWhen: currentCacheInvalidationActions,
          },
        );
      }

      return updatedPayload;
    });
  }
}
